import { useContext, useEffect, useMemo, useState } from 'react';

import { FirebaseContext } from '@/session/FirebaseContext';
import type { Dict } from '@/helpers';
import { ensureLeaderboardCache } from '@/helpers/leagues/leagueLeaderboardCache';
import { buildStandingsSeries } from '@/helpers/leagues/standingsSeries';
import { useLeagueHistory } from '@/hooks/useLeagueHistory';
import { useWc26GameResults } from '@/hooks/useWc26GameResults';
import type League from '@/models/league';
import type { leagueMember } from '@/models/user';

export function useLeagueStandingsChartData(league: League & { id: string }) {
  const ctx = useContext(FirebaseContext);
  const gameResults = useWc26GameResults();
  const { columns, loading, error } = useLeagueHistory(league.id);

  const [membersByUid, setMembersByUid] = useState<Dict<leagueMember>>({});
  useEffect(() => {
    let cancelled = false;
    void ensureLeaderboardCache(ctx.db, league)
      .then((entry) => {
        if (!cancelled) setMembersByUid(entry.membersByUid);
      })
      .catch(() => {
        /* names fall back to "Player" */
      });
    return () => {
      cancelled = true;
    };
  }, [ctx.db, league]);

  const standings = useMemo(() => buildStandingsSeries(columns, membersByUid), [columns, membersByUid]);

  const ready = !loading && !error && standings.maxMatchday >= 1 && standings.series.length > 0;

  return {
    columns,
    standings,
    loading,
    error,
    ready,
    currentUid: ctx.user?.uid,
    gameResults,
  };
}
